import { useQuery } from '@tanstack/react-query';
// mui
import { Box, CircularProgress, Drawer } from '@mui/material';
import { makeStyles } from '@mui/styles';

// lodash
import _ from 'lodash';
// actions
import { getQuote } from '../../../../inbox/QuotesService';
// utils
import { formatPrimeXQuote, formatQuote } from '../../quotes.services';
import { determineIsLegacyProduct } from '../../../agencies/quotes/PrimeX/utils/primeX.util';
import { getPrimeXQuoteDetails } from '../../../../api';
import { getConsolidatedP100CoverageKeys } from '../../../workflows/quotes/prime-100/utils/coverages';

export default function QuoteDrawer({
  open,
  onClose,
  quoteId,
  product,
  children,
}: any) {
  const classes = useStyles();

  const getQuoteDetails = () =>
    determineIsLegacyProduct(product)
      ? getQuote(quoteId, product)
      : getPrimeXQuoteDetails(product, quoteId, {
          params: {
            view: 'full',
          },
        });

  const { data: quote, isInitialLoading } = useQuery({
    queryKey: ['quote', quoteId],
    queryFn: () =>
      getQuoteDetails().then(({ data }) => {
        return determineIsLegacyProduct(product)
          ? formatQuote(filterQuote(data))
          : formatPrimeXQuote(data.data);
      }),
    refetchOnWindowFocus: false,
    enabled: open,
  });

  return (
    <Drawer
      // @ts-expect-error TS(2322): Type '{ children: any[]; pt: number; anchor: "righ... Remove this comment to see the full error message
      pt={2}
      anchor="right"
      open={open}
      onClose={onClose}
      classes={classes}
    >
      {isInitialLoading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="13rem"
          width="42rem"
        >
          <CircularProgress size="5rem" />
        </Box>
      )}
      {!isInitialLoading && typeof children === 'function'
        ? children({ quote })
        : null}
    </Drawer>
  );
}

const useStyles = makeStyles(({ palette }) => ({
  paper: {
    height: 'auto',
    minWidth: '57rem',
    maxHeight: 'calc(100% - 12rem)',
    padding: '1rem',
    position: 'fixed',
    top: '6rem',
    right: 17,
    zIndex: 10,
    overflow: 'visible',
    color: palette.primary.main,
    backgroundColor: palette.background.paper,
    borderRadius: '0.9rem',
  },
}));

const filterQuote = (quote: any) => {
  return {
    ..._.pick(quote, [
      'currency',
      'id',
      'quoteNumber',
      'policyNumber',
      'accountId',
      'premium',
      'agentFirstName',
      'agentLastName',
      'agentEmail',
      'agentPhone',
      'customerDescription',
      'customerStatus',
      'customerEmail',
      'policyId',
      'isRenewal',
      'status',
      'companyName',
      'noKnownLossLetterReq',
      'created',
      'modified',
      'totalPremium',
      'product',
      'isSurplus',
      'deductible',
      'agencyName',
      'expiresOn',
      'boundOn',
      'coveragesAdded',
      'coveragesUpdated',
      'coveragesRemoved',
      'boostRaterVersion',
    ]),

    ..._.pick(quote.firmographicData, ['naicsCode', 'primaryIndustry']),
    ...(quote.quoteNumber.includes('100') && {
      ..._.pick(quote.initialRequestData, [
        'limit',
        'effectiveDate',
        'deductible',
        'isEndorsement',
        'additionalInsureds',
        'address',
        'companyName',
      ]),
      coverages: {
        ..._.pick(quote.initialRequestData, getConsolidatedP100CoverageKeys()),
      },
    }),
    ...(quote.quoteNumber.includes('250') && {
      ..._.pick(quote.initial250RequestData, [
        'limit',
        'effectiveDate',
        'deductible',
        'isEndorsement',
        'coverages',
        'additionalInsureds',
        'endDate',
        'address',
      ]),
    }),
  };
};
